import { useState, useEffect, useMemo, startTransition } from 'react';
import { isDemoBrand } from '../utils/constants';
import { formatDateForAPI } from '../utils/formatters';
import { fetchWithBrand } from '../utils/api';

/**
 * Fetches unified Meta Ads + GA4 daily performance based on dateRange.
 * Returns raw data and a computed summary.
 */
export function useMetaAds(brandId, dateRange, startDate, endDate) {
  const [unifiedData, setUnifiedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Guard: skip if custom range is incomplete
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchData = async () => {
      // Demo data
      if (isDemoBrand(brandId)) {
        setIsLoading(true);
        const demo = [
          { date: '2025-09-01', clicks: 900, ga4_sessions: 2200, ga4_total_revenue: 120000, conversion_value: 95000, spend: 35000, campaign_id: 'M-1' },
          { date: '2025-10-01', clicks: 1100, ga4_sessions: 2600, ga4_total_revenue: 140000, conversion_value: 115000, spend: 38000, campaign_id: 'M-2' },
        ];
        setUnifiedData(demo);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        let url;
        const effectiveStart = dateRange === 'custom' ? startDate : null;
        const effectiveEnd = dateRange === 'custom' ? endDate : null;
        if (effectiveStart && effectiveEnd) {
          const startDateStr = formatDateForAPI(effectiveStart);
          const endDateStr = formatDateForAPI(effectiveEnd);
          url = `/api/google-ads/unified-meta-ga4-performance?dateRange=custom&startDate=${startDateStr}&endDate=${endDateStr}`;
        } else {
          url = `/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}`;
        }

        const response = await fetchWithBrand(url, brandId);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        startTransition(() => {
          setUnifiedData(data);
        });
      } catch {
        setUnifiedData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [brandId, dateRange, startDate, endDate]);

  // Compute summary from unified data
  const metaAdsSummary = useMemo(() => {
    if (!unifiedData || unifiedData.length === 0) {
      return {
        totalClicks: 0,
        totalSessions: 0,
        totalConversions: 0,
        totalGa4Purchases: 0,
        totalGa4Revenue: 0,
        totalMetaRevenue: 0,
        totalSpend: 0,
        campaignCount: 0,
        cpc: 0,
        cps: 0,
        roas: 0,
      };
    }

    const totals = unifiedData.reduce((acc, item) => {
      acc.totalClicks += item.clicks || 0;
      acc.totalSessions += item.ga4_sessions || 0;
      acc.totalConversions += item.conversions || 0;
      acc.totalGa4Purchases += item.ga4_purchases || 0;
      acc.totalGa4Revenue += item.ga4_total_revenue || 0;
      acc.totalMetaRevenue += item.conversion_value || 0;
      acc.totalSpend += item.spend || 0;
      return acc;
    }, {
      totalClicks: 0,
      totalSessions: 0,
      totalConversions: 0,
      totalGa4Purchases: 0,
      totalGa4Revenue: 0,
      totalMetaRevenue: 0,
      totalSpend: 0,
    });

    const campaignIds = new Set(unifiedData.map(item => item.campaign_id));
    const cpc = totals.totalClicks > 0 ? totals.totalSpend / totals.totalClicks : 0;
    const cps = totals.totalSessions > 0 ? totals.totalSpend / totals.totalSessions : 0;
    const roas = totals.totalSpend > 0 ? totals.totalMetaRevenue / totals.totalSpend : 0;

    return {
      ...totals,
      campaignCount: campaignIds.size,
      cpc,
      cps,
      roas,
    };
  }, [unifiedData]);

  return {
    unifiedData,
    metaAdsSummary,
    isLoading,
  };
}
